/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { GroupedList, IGroup, IGroupHeaderProps } from '@fluentui/react/lib/GroupedList';
import { FontIcon, IFontIconProps } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { AddExampleListItem } from 'src/luis/verticals/getStartedGuide/components/AddExampleListItem';
import { DesignListItem } from 'src/luis/verticals/getStartedGuide/components/DesignListItem';
import { IterateListItem } from 'src/luis/verticals/getStartedGuide/components/IterateListItem';
import { PublishListItem } from 'src/luis/verticals/getStartedGuide/components/PublishListItem';
import { TrainTestListItem } from 'src/luis/verticals/getStartedGuide/components/TrainTestListItem';

const messages = defineMessages({
    designSchemaTableCell: {
        id: 'portal.getStartedList.designSchemaTableCell',
        defaultMessage: 'DESIGN YOUR SCHEMA'
    },
    buildAppTableCell: {
        id: 'portal.getStartedList.buildAppTableCell',
        defaultMessage: 'BUILD YOUR APP'
    },
    addExamplesTableCell: {
        id: 'portal.getStartedList.addExamplesTableCell',
        defaultMessage: '1. Add examples'
    },
    trainAndTestTableCell: {
        id: 'portal.getStartedList.trainAndTestTableCell',
        defaultMessage: '2. Train and Test'
    },
    publishTableCell: {
        id: 'portal.getStartedList.publishTableCell',
        defaultMessage: '3. Publish'
    },
    iterateTableCell: {
        id: 'portal.getStartedList.iterateTableCell',
        defaultMessage: 'ITERATE'
    }
});

const { styleComponent } = getStylistV2('GetStartedGuide');

export const StyledStack = styleComponent<IStackProps>(Stack)('StyledStack', { padding: '0px 15px 15px 15px' });

const StyledFontIcon = styleComponent<IFontIconProps>(FontIcon)('StyledFontIcon', theme => ({
    color: theme.checkMarkColor,
    fontSize: 15,
    lineHeight: '40px',
    padding: 5
}));

const StyledHeader = styleComponent<IStackProps>(Stack)(
    'StyledHeader',
    (theme, cssVars) => ({
        height: 40,
        paddingLeft: 15,
        paddingRight: 15,
        cursor: 'pointer',
        backgroundColor: cssVars.backgroundColor,
        boxShadow: theme.tableHeaderShadow,
        fontSize: '15px',
        fontWeight: 400,
        boxSizing: 'border-box',
        justifyContent: 'flex-start',
        alignItems: 'center'
    }),
    {
        cssVars: { backgroundColor: '' },
        cssVarsSetter: ({ isCollapsed }: { isCollapsed: boolean }, theme) => ({
            backgroundColor: isCollapsed ? theme.headerCollapsedColor : theme.headerExpandedColor
        })
    }
);

const headerKeys = {
    design: 'design',
    build: 'build',
    addExample: 'addExample',
    trainTest: 'trainTest',
    publish: 'publish',
    iterate: 'iterate'
};

const collapseAllOtherGroups = (thisGroup: IGroup, otherGroups: IGroup[]) => {
    // Closing all other expanded groups
    for (const group of otherGroups) {
        if (!group.isCollapsed && group !== thisGroup) {
            let isParent = false;
            // Finding out if expanded group is parent group
            for (const subGroup of group.children) {
                if (subGroup === thisGroup) {
                    isParent = true;
                    break;
                }
            }
            // Making sure not to close parent group and closing sibling groups
            if (isParent) {
                for (const subGroup of group.children) {
                    if (subGroup !== thisGroup) {
                        subGroup.isCollapsed = true;
                    }
                }
            } else {
                group.isCollapsed = true;
            }
        }
    }
};

export const GetStartedList = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const currentVersionedApp = rootStore?.currentLuisAppStore?.currentAppVersionStore;
    const utterancesCount = currentVersionedApp?.intentStoreHouse?.items?.reduce((acc, curr) => {
        acc += curr.labelsCount;

        return acc;
    }, 0);
    const isTrained = currentVersionedApp?.isTrained;
    const publishedDate = currentVersionedApp?.publishedDate;

    const createItemsList = (): JSX.Element[] => [
        <DesignListItem />,
        <AddExampleListItem />,
        <TrainTestListItem />,
        <PublishListItem />,
        <IterateListItem />
    ];

    const createGroupList = (): IGroup[] => [
        {
            name: localize(messages.designSchemaTableCell),
            key: headerKeys.design,
            startIndex: 0,
            count: 1,
            isCollapsed: true,
            children: []
        },
        {
            name: localize(messages.buildAppTableCell),
            key: headerKeys.build,
            startIndex: -1,
            count: 0,
            isCollapsed: true,
            children: [
                {
                    name: localize(messages.addExamplesTableCell),
                    key: headerKeys.addExample,
                    startIndex: 1,
                    count: 1,
                    isCollapsed: true,
                    children: []
                },
                {
                    name: localize(messages.trainAndTestTableCell),
                    key: headerKeys.trainTest,
                    startIndex: 2,
                    count: 1,
                    isCollapsed: true,
                    children: []
                },
                {
                    name: localize(messages.publishTableCell),
                    key: headerKeys.publish,
                    startIndex: 3,
                    count: 1,
                    isCollapsed: true,
                    children: []
                }
            ]
        },
        { name: localize(messages.iterateTableCell), key: headerKeys.iterate, startIndex: 4, count: 1, isCollapsed: true, children: [] }
    ];

    const items = createItemsList();
    const groups = createGroupList();

    const onRenderCell = (_: number, item: JSX.Element, itemIndex: number): JSX.Element => (
        <div data-automation-id="item" data-selection-index={itemIndex}>
            {item}
        </div>
    );

    const onRenderCheckMark = (key: string): JSX.Element => {
        switch (key) {
            case headerKeys.addExample:
                return (
                    <StyledFontIcon
                        data-automation-id="addExampleCheckMark"
                        iconName={utterancesCount ? 'SkypeCircleCheck' : 'CircleRing'}
                    />
                );
            case headerKeys.trainTest:
                return (
                    <StyledFontIcon
                        data-automation-id="trainTestCheckMark"
                        iconName={utterancesCount && isTrained ? 'SkypeCircleCheck' : 'CircleRing'}
                    />
                );
            case headerKeys.publish:
                return (
                    <StyledFontIcon
                        data-automation-id="publishCheckMark"
                        iconName={utterancesCount && isTrained && publishedDate ? 'SkypeCircleCheck' : 'CircleRing'}
                    />
                );

            default:
                return <> </>;
        }
    };

    const onRenderHeader = (headerProps: IGroupHeaderProps): JSX.Element => {
        const toggleCollapse = (): void => {
            collapseAllOtherGroups(headerProps.group, groups);
            headerProps.onToggleCollapse(headerProps.group);
        };

        return (
            <StyledHeader
                customProps={{ isCollapsed: headerProps.group?.isCollapsed }}
                data-automation-id="header"
                horizontal
                verticalAlign="center"
                horizontalAlign="start"
                onClick={toggleCollapse}
                role="button">
                {onRenderCheckMark(headerProps.group.key)}
                {headerProps.group.name}
            </StyledHeader>
        );
    };

    return (
        <GroupedList
            items={items}
            onRenderCell={onRenderCell}
            groups={groups}
            groupProps={{
                onRenderHeader,
                showEmptyGroups: true
            }}
        />
    );
});
